import { ScoreComputation, computeScore } from './scoring';

interface KeptDiceScoreInput {
  dice: number[];
  keptMask: boolean[];
  capturedMask: boolean[];
  diceOrigins?: number[];
  lastRollIndex?: number | null;
}

export interface KeptDiceScoreResult {
  keptDice: number[];
  keptIndices: number[];
  score: ScoreComputation;
  nonScoringKept: number;
}

function resolveLastRollIndex(
  explicit: number | null | undefined,
  diceOrigins: number[] | undefined,
  keptIndices: number[],
): number | undefined {
  if (typeof explicit === 'number') return explicit;
  if (!diceOrigins || diceOrigins.length === 0) return undefined;
  const candidates = keptIndices
    .map((idx) => diceOrigins[idx])
    .filter((v) => typeof v === 'number') as number[];
  if (candidates.length === 0) return undefined;
  return Math.max(...candidates);
}

/**
 * Scores kept dice using diceOrigins to isolate current-roll scoring.
 */
export function scoreKeptDiceWithOrigins(input: KeptDiceScoreInput): KeptDiceScoreResult {
  const { dice, keptMask, capturedMask, diceOrigins, lastRollIndex } = input;
  const keptIndices = dice
    .map((_, idx) => (keptMask[idx] && !capturedMask[idx] ? idx : -1))
    .filter((idx) => idx >= 0);
  const keptDice = keptIndices.map((idx) => dice[idx]);
  if (keptIndices.length === 0) {
    return {
      keptDice: [],
      keptIndices: [],
      score: computeScore([]),
      nonScoringKept: 0,
    };
  }

  const resolvedLastRoll = resolveLastRollIndex(lastRollIndex, diceOrigins, keptIndices);
  if (typeof resolvedLastRoll !== 'number') {
    throw new Error('Kept dice scoring requires dice origin metadata');
  }
  const isCurrentRollDie = (idx: number) => {
    const origin = diceOrigins?.[idx];
    if (typeof origin !== 'number') {
      throw new Error('Kept dice scoring requires dice origin metadata');
    }
    return origin === resolvedLastRoll;
  };

  const currentRollIndices = keptIndices.filter((idx) => isCurrentRollDie(idx));
  const previousRollIndices = keptIndices.filter((idx) => !isCurrentRollDie(idx));
  const currentRollDice = currentRollIndices.map((idx) => dice[idx]);

  const baseScore = computeScore(currentRollDice);
  const lines = baseScore.breakdown
    .slice(0, -1)
    .filter((line) => !/^brak/i.test(line));
  const details = [...baseScore.breakdownDetails];
  let scoringDiceCount = baseScore.scoringDiceCount;
  let totalPoints = baseScore.basePoints;

  const previousSinglesCount: Record<number, number> = {};
  previousRollIndices.forEach((idx) => {
    const val = dice[idx];
    if (val === 1 || val === 5) {
      previousSinglesCount[val] = (previousSinglesCount[val] || 0) + 1;
    }
  });

  Object.entries(previousSinglesCount).forEach(([valStr, count]) => {
    if (!count) return;
    const val = Number(valStr);
    const pts = count * (val === 1 ? 10 : 5);
    totalPoints += pts;
    scoringDiceCount += count;
    lines.push(`${count}×${val} (z poprzednich rzutów): ${pts}`);
    details.push({ kind: 'single', value: val, count, points: pts });
  });

  if (lines.length === 0) lines.push('Brak punktujących kości');
  lines.push(`Razem: ${totalPoints}`);

  return {
    keptDice,
    keptIndices,
    score: {
      basePoints: totalPoints,
      scoringDiceCount,
      breakdown: lines,
      breakdownDetails: details,
    },
    nonScoringKept: Math.max(0, keptDice.length - scoringDiceCount),
  };
}
